/** @format */

import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { ReactComponent as BoxArrowUpRight } from "bootstrap-icons/icons/box-arrow-up-right.svg";

/** Definintion of resource type icons */
import { ReactComponent as BLCircles } from "../../../assets/icons/resource-types/blended-learning.svg";
import { ReactComponent as BLPLus } from "../../../assets/icons/resource-types/blended-learning-plus.svg";
import { ReactComponent as ClassRoom } from "../../../assets/icons/resource-types/classroom-resource.svg";
import { ReactComponent as DiagnosticTool } from "../../../assets/icons/resource-types/diagnostic-tool.svg";
import { ReactComponent as ExamWalkThrough } from "../../../assets/icons/resource-types/exam-walk-through.svg";
import { ReactComponent as KnowledgeOrganiser } from "../../../assets/icons/resource-types/knowledge-organiser.svg";
import { ReactComponent as LessonPlan } from "../../../assets/icons/resource-types/lesson-plan.svg";
import { ReactComponent as ExamReview } from "../../../assets/icons/resource-types/online-exam-review.svg";
import { ReactComponent as OtherResources } from "../../../assets/icons/resource-types/other-resources.svg";
import { ReactComponent as QuestionBank } from "../../../assets/icons/resource-types/question-bank.svg";
import { ReactComponent as StudentPlanner } from "../../../assets/icons/resource-types/student-planner.svg";
import { ReactComponent as TeacherPlanningIcon } from "../../../assets/icons/resource-types/lesson-plan.svg";

import { useTranslation } from "react-i18next";
import "../resourceIndexUI/ResourcesIndexUI.scss";
import "./ResourceCard.scss";
import { Markup } from "interweave";
import StarResource from "../../starResource/StarResource";
import {
  ResourceLink,
  ResourceCard as ResourceCardType,
  ResourceType,
} from "../../../types";
import { Button, ButtonGroup } from "react-bootstrap";
import SpecItemBadge from "./SpecItemBadge";
import { User } from "../../../app/services/userApi";
import { getDefaultResourceDescription } from "../resourceDetails/defaultDescriptions";
import { useLocale } from "../../../i18n/hooks";

type Props = {
  resource: ResourceCardType;
  imgPath: string;
  acknowledgements?: ResourceLink;
  user?: User;
};

const ResourceCard: React.FC<Props> = ({
  resource,
  imgPath,
  acknowledgements,
  user,
}) => {
  //const brand = useSelector(selectBrand()); Unused - 'Eduqas' branding not available
  const { t: _ } = useTranslation();
  const locale = useLocale();
  const [starred, setStarred] = useState(false); // TODO: ADD isStarred to user Object

  const description =
    resource.Object.description !== undefined
      ? resource.Object.description
      : getDefaultResourceDescription(
          locale,
          resource.Type as ResourceType,
          resource.Object.title,
        );
  const synopsis =
    resource.Object.synopsis !== undefined
      ? resource.Object.synopsis
      : description?.substring(
          0,
          description.indexOf(".") !== -1 ? description.indexOf(".") : undefined,
        ); // Use the first sentence of the description

  const isStaff = user !== undefined && (user.role === "wjec" || user.role === "admin");

  const getRefLink = useCallback(() => {
    let link = `r/${resource.Code}`;
    if (resource.Copy !== undefined) {
      // user resource
      link += `/${resource.Copy}`;
    } else {
      // resource
      link += `/${resource.Type}/${resource.Qualification?.toUpperCase()}`;
    }
    return link;
  }, [resource.Code, resource.Copy, resource.Qualification, resource.Type]);

  const getResourceLink = () => {
    const link = resource.Object.links[0].src;

    if (resource.Copy === undefined) {
      return link;
    }

    const codeIndex = link.toUpperCase().indexOf(resource.Code.toUpperCase());
    if (codeIndex === -1) {
      return link;
    }

    const baseUrl = link.substring(0, codeIndex);

    if (user === undefined) {
      return link;
    }

    return `${baseUrl}u/${user.emailHash}/${resource.Code}/${resource.Copy}`;
  };

  return (
    <div className={`card resource-card h-100 shadow`}>
      <div
        role="img"
        className="card-img-top border-bottom position-relative bg-secondary"
        style={{
          height: 0,
          paddingTop: "56.25%",
          backgroundImage: `
            linear-gradient(0deg, rgba(25,25,25,.75) 0%,
            rgba(25,25,25,0) 35%), 
            url(${resource.Object.thumbnail}) 
          `,
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      >
        <Link
          to={getRefLink()}
          className="position-absolute top-0 left-0 w-100 h-100"
          title={_("More info")}
        ></Link>
        <div className="position-absolute badge-container d-flex flex-column top-0 left-0 w-75 gap-2 mt-2">
          <SpecItemBadge resource={resource} />
          {Array.isArray(resource.Object.levels) &&
            resource.Object.levels.map((level: string) => (
              <span key={level}>
                <small
                  className={`bg-dark card-badge d-inline-block px-2  rounded-end shadow-sm text-white bg-${level.toLowerCase()}`}
                >
                  {_(level)}
                </small>
                <br />
              </span>
            ))}
          {resource.Object.categories?.map((cat: string) => (
            <span key={cat}>
              <small
                className={`text-capitalize card-badge bg-light d-inline-block px-2 rounded-end shadow-sm text-dark bg-${cat
                  .replace(" ", "-")
                  .toLowerCase()}`}
              >
                {cat}
              </small>
              <br />
            </span>
          ))}
        </div>
        <div className="star-container position-absolute top-0 end-0 px-2 py-2 d-flex align-items-center">
          <StarResource
            isStarred={starred}
            onClick={() => setStarred(!starred)}
          />
          {/* {<><StarFilled title={starred ? _("Un-star?"): _("Star?")} className={starred ? "star filled": "star"} onClick={() => {setStarred(!starred)}}/></>} */}
        </div>
        <div
          className="text-warning position-absolute bottom-0 end-0 display-3 mx-2 my-2 d-flex align-items-center resource-icon resource-icon-bg"
          // onClick={() => handleTypeClick(resource.type)}
        >
          {/* {resource.type === type && <span className="fs-6"><X /></span>} */}
          {resource.Type === "BL" &&
            (resource.PK.includes("USER:") ? (
              <BLPLus className="bi" title={_("Blended Learning Plus")} />
            ) : (
              <BLCircles className="bi" title={_("Blended Learning")} />
            ))}
          {resource.Type === "CR" && (
            <ClassRoom className="bi" title={_("Classroom Resource")} />
          )}
          {resource.Type === "DT" && (
            <DiagnosticTool className="bi" title={_("Diagnostic Tool")} />
          )}
          {resource.Type === "EWT" && (
            <ExamWalkThrough className="bi" title={_("Exam Walk Through")} />
          )}
          {resource.Type === "KO" && (
            <KnowledgeOrganiser
              className="bi"
              title={_("Knowledge Organiser")}
            />
          )}
          {resource.Type === "LP" && (
            <LessonPlan className="bi" title={_("Lesson Plan")} />
          )}
          {resource.Type === "OER" && (
            <ExamReview className="bi" title={_("Online Exam Review")} />
          )}
          {resource.Type === "OR" && (
            <OtherResources className="bi" title={_("Other Resources")} />
          )}
          {resource.Type === "QB" && (
            <QuestionBank className="bi" title={_("Question Bank")} />
          )}
          {resource.Type === "SP" && (
            <StudentPlanner className="bi" title={_("Student Planner")} />
          )}
          {resource.Type === "TP" && (
            <TeacherPlanningIcon className="bi" title={_("Teacher Planning")} />
          )}
        </div>
      </div>
      <div className="card-body position-relative d-flex flex-column">
        <div
          className="position-absolute top-0 left-0 d-flex gap-2"
          style={{ marginTop: "-10px" }}
        >
          {!resource.PK.includes("USER:") &&
            isStaff &&
            resource.Status === "draft" && (
              <div
                role="status"
                aria-label="draft"
                className="badge  badge-editing bg-editing d-flex px-2"
              >
                {_("draft")}
              </div>
            )}
          {resource.Qualification?.startsWith("mfw") && (
            <div
              role="status"
              aria-label="draft"
              className="badge badge-light bg-primary d-flex px-2"
            >
              {_("new")}
            </div>
          )}
        </div>

        <Markup
          tagName="h5"
          className="card-title"
          content={resource.Object.title}
        />
        <Markup tagName="div" className="card-text mb-3" content={synopsis} />

        <div className="btn-group mt-auto" role="group">
          <ButtonGroup>
            <Button variant="light">
              <Link
                to={getRefLink()}
                title={_("More info")}
                className="text-dark"
              >
                {_("More info")}
              </Link>
            </Button>
            {resource.Object.links &&
              resource.Object.links.length === 1 &&
              resource.Object.links.map((file: ResourceLink, index: number) => {
                return (
                  file !== acknowledgements &&
                  file.src && (
                    <a
                      title={_("Open resource")}
                      key={file.src}
                      href={getResourceLink()}
                      data-testid="resourceLink"
                      target="_blank"
                      rel="noreferrer"
                      // disable while the user is still loading or if no user is found to prevent incorrect link
                      className={`btn btn-primary btn-wjec text-light ${
                        resource.Copy !== undefined &&
                        user === undefined &&
                        "disabled"
                      }`}
                    >
                      {_("Open resource")}
                      &nbsp;
                      <BoxArrowUpRight />
                      {/* {resource.type==='BL' && (
                    <a href={file.src.replace(/https?:\/\/([^/]+)\//,'https://$1/print/')} title={_('Print resource')} target="_blank" rel="noreferrer" className="btn btn-primary text-light border-start border-end">
                      <Printer/>
                      &nbsp;&nbsp;
                      <BoxArrowUpRight />
                    </a>
                  )} */}
                    </a>
                  )
                );
              })}
          </ButtonGroup>
        </div>
      </div>
      <div className="card-footer">
        <div className="text-muted">
          <small>
            {_("Last Updated")}:{" "}
            <time role="time" dateTime={resource.lastLog?.time ?? resource.lastEdit?.time}>
              {new Date(resource.lastLog?.time ?? resource.lastEdit?.time ?? "").toLocaleDateString()}
            </time>
          </small>
          <div className="float-end">
            {resource.Object.acknowledgements?.length > 0 &&
              (Array.isArray(resource.Object.acknowledgements) ? (
                resource.Object.acknowledgements.map(
                  (acknowledgement: ResourceLink) =>
                    acknowledgement?.src &&
                    acknowledgement?.label && (
                      <a
                        title={acknowledgement.label}
                        key={acknowledgement.src + acknowledgement.label}
                        className="btn btn-link btn-sm"
                        target="_blank"
                        rel="noreferrer"
                        href={acknowledgement.src}
                      >
                        {acknowledgement.label} <BoxArrowUpRight />
                      </a>
                    ),
                )
              ) : (
                <a
                  title={_("Acknowledgements")}
                  className="btn btn-link btn-sm"
                  target="_blank"
                  rel="noreferrer"
                  href={resource.Object.acknowledgements}
                >
                  {_("Acknowledgements")} <BoxArrowUpRight />
                </a>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;
